import React, { useState, useEffect } from "react";
import Select from "react-select";

const EnergyCalculator = ({ items }) => {
  // State initialization with localStorage loading
  const [selectedEnergyItem, setSelectedEnergyItem] = useState(null);
  const [targetEnergy, setTargetEnergy] = useState(() => {
    const saved = localStorage.getItem('energyCalculator_targetEnergy');
    return saved ? parseInt(saved) : 100;
  });
  const [result, setResult] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Save state to localStorage when it changes
  useEffect(() => {
    if (!isInitialized) return;
    
    localStorage.setItem('energyCalculator_targetEnergy', targetEnergy.toString());
    
    if (selectedEnergyItem) {
      localStorage.setItem('energyCalculator_selectedItem', JSON.stringify({
        value: selectedEnergyItem.value,
        label: selectedEnergyItem.label,
        energyValue: selectedEnergyItem.energyValue,
        image: selectedEnergyItem.image
      }));
    } else {
      localStorage.removeItem('energyCalculator_selectedItem');
    }
  }, [selectedEnergyItem, targetEnergy, isInitialized]);

  // Load state from localStorage when component mounts
  useEffect(() => {
    if (!items) return;

    try {
      // Load selected item
      const savedItem = localStorage.getItem('energyCalculator_selectedItem');
      if (savedItem) {
        const parsedItem = JSON.parse(savedItem);
        const matchingItem = items.find(item => item.id === parsedItem.value);
        if (matchingItem) {
          setSelectedEnergyItem({
            value: matchingItem.id,
            label: matchingItem.name,
            energyValue: matchingItem.energyValue,
            image: matchingItem.image,
            data: matchingItem
          });
        }
      }
      
      setIsInitialized(true);
    } catch (e) {
      console.error("Failed to load saved state", e);
      setIsInitialized(true);
    }
  }, [items]);

  // Filter energy items (positive energy values)
  const energyItems = items
    ? items
        .filter((item) => item.energyValue > 50)
        .sort((a, b) => b.energyValue - a.energyValue)
        .map((item) => ({
          value: item.id,
          label: item.name,
          energyValue: item.energyValue,
          image: item.image,
          data: item,
        }))
    : [];

  // Custom filter function to only search by name
  const filterOption = (option, rawInput) => {
    const input = rawInput.toLowerCase();
    return option.label.toLowerCase().includes(input);
  };

  const formatOptionLabel = ({ label, energyValue, image }) => (
    <div className="flex items-center gap-2">
      <img src={image} alt={label} className="w-6 h-6" />
      <span>
        {label} (+{energyValue} Energy)
      </span>
    </div>
  );

  // Calculate required items and cost
  useEffect(() => {
    if (!selectedEnergyItem || !targetEnergy) {
      setResult(null);
      return;
    }

    const energyPerItem = selectedEnergyItem.energyValue;
    const itemsNeeded = Math.ceil(targetEnergy / energyPerItem);
    const totalEnergy = energyPerItem * itemsNeeded;
    const storePrice = selectedEnergyItem.data?.stores?.[0]?.price || 0;
    const costPerItem = selectedEnergyItem.data?.marketPrice || 0;
    const totalCost = !isNaN(costPerItem) ? itemsNeeded * costPerItem : "N/A";
    const totalStoreCost = storePrice ? itemsNeeded * storePrice : "N/A";
    const craftingCost = selectedEnergyItem.data?.craftingCost ? parseFloat(selectedEnergyItem.data.craftingCost) : NaN;
    const totalCraftingCost = !isNaN(craftingCost) ? craftingCost * itemsNeeded : "N/A";    
    const energyPerCoinMP = !isNaN(totalCost) ? totalCost / totalEnergy : "N/A";
    const energyPerCoinStore = !isNaN(totalStoreCost) ? totalStoreCost / totalEnergy  : "N/A";
    const energyPerCoinCraft = !isNaN(totalCraftingCost) ? totalCraftingCost  / totalEnergy : "N/A";

    setResult({
      itemsNeeded,
      totalCost,
      energyPerItem,
      totalStoreCost,
      totalCraftingCost,
      energyPerCoinCraft,
      energyPerCoinMP,
      energyPerCoinStore,
      totalEnergy
    });
  }, [selectedEnergyItem, targetEnergy]);

  return (
    <div className="calculator-style">
      <h2 className="calculator-title">Energy Calculator</h2>

      <div className="calculator-project">
        <div className="form-group">
          <label className="block mb-2 font-medium">Energy Item</label>
          <Select
            options={energyItems}
            value={selectedEnergyItem}
            onChange={setSelectedEnergyItem}
            placeholder="Select energy item..."
            filterOption={filterOption}
            formatOptionLabel={formatOptionLabel}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            className="calculator-form"
          />
        </div>

        <div className="form-group">
          <label className="block mb-2 font-medium">Energy to Gain</label>
          <input
            type="number"
            min="1"
            value={targetEnergy}
            onChange={(e) => setTargetEnergy(parseInt(e.target.value) || 0)}
            className="w-full p-2 border rounded calculator-form"
          />
        </div>

        {result && (
          <div className="mt-4 p-4 bg-[var(--panel-section-bg)] rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div className="calc-card">
                <div className="text-sm">Items Needed</div>
                <div className="text-base font-bold">{result.itemsNeeded}</div>
              </div>
              <div className="calc-card">
                <div className="text-sm">Energy Per Item</div>
                <div className="text-base font-bold">+{result.energyPerItem}</div>
                <div className="text-sm">Total Energy</div>
                <div className="text-base font-bold">+{result.totalEnergy}</div>
              </div>
              <div className="calc-card">
                <div className="text-sm">Total Cost</div>
                <div className="text-base font-bold">MP: {typeof result.totalCost === 'number' ? result.totalCost.toLocaleString() : result.totalCost} coins</div>
                <div className="text-base font-bold">Store: {typeof result.totalStoreCost === 'number' ? result.totalStoreCost.toLocaleString() : result.totalStoreCost} coins</div>
                <div className="text-base font-bold">Craft: {typeof result.totalCraftingCost === 'number' ? result.totalCraftingCost.toLocaleString() : result.totalCraftingCost} coins</div>
              </div>
              <div className="calc-card">
                <div className="text-sm">Coin Per Energy</div>
                <div className="text-base font-bold">MP: {typeof result.energyPerCoinMP === 'number' ? result.energyPerCoinMP.toLocaleString() : result.energyPerCoinMP} coins</div>
                <div className="text-base font-bold">Store: {typeof result.energyPerCoinStore === 'number' ? result.energyPerCoinStore.toLocaleString() : result.energyPerCoinStore} coins</div>
                <div className="text-base font-bold">Craft: {typeof result.energyPerCoinCraft === 'number' ? result.energyPerCoinCraft.toLocaleString() : result.energyPerCoinCraft} coins</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnergyCalculator;